const $inject = [
  '$q',
  '$http',
  '$cookies',
  '$httpParamSerializer',
  'moment',
  'settingsService',
  'userProfileService',
  'userDebtorService',
  'apiService',
  'APP_CONFIG',
  'angularAuth0',
];

const authService = function (
  $q,
  $http,
  $cookies,
  $httpParamSerializer,
  moment,
  settingsService,
  userProfileService,
  userDebtorService,
  apiService,
  APP_CONFIG,
  angularAuth0
) {
  let service = {};
  service.getFullName = getFullName;
  service.login = login;
  service.logout = logout;
  service.refresh = refresh;
  service.signUp = signUp;
  service.reset = reset;
  service.forgot = forgot;
  service.storeAuth = storeAuth;
  service.getOauth = getOauth;
  service.clearOauth = clearOauth;
  service.getAccessToken = getAccessToken;
  service.getRefreshToken = getRefreshToken;
  service.isAuthenticated = isAuthenticated;
  service.loginImpersonation = loginImpersonation;
  service.getImperonationOauth = getImperonationOauth;
  service.revertImpersonation = revertImpersonation;
  service.encodeUsername = encodeUsername;
  service.decodeUsername = decodeUsername;
  service.redirectTo = redirectTo;
  const oauthImpersonatorKey = 'oauthImpersonator';

  function getFullName() {
    return service.getOauth().fullName;
  }

  function login(username, password) {
    let data = {
      client_id: APP_CONFIG.client.id,
      client_secret: APP_CONFIG.client.secret,
      grant_type: 'password',
      username: username,
      password: password,
    };

    let req = {
      isPublic: true,
      withCredentials: true,
      method: 'POST',
      url: APP_CONFIG.api.token,
      headers: {
        'Content-type': 'application/x-www-form-urlencoded; charset=utf-8',
      },
      data: $httpParamSerializer(data),
    };

    return $http(req).then(function (data, status, header) {
      let results = [];
      results.headers = data.headers();
      $cookies.put('.AspNet.Application', results.headers.lightningbook_web_cookie);
      storeAuth(data.data);
    });
  }

  function storeAuth(auth) {
    auth.expires_at = moment().add(auth.expires_in, 's').format('x');
    localStorage.setItem('oauth', JSON.stringify(auth));
    return getOauth();
  }

  function logout() {
    let deferred = $q.defer();

    service.clearOauth();
    settingsService.clear();
    userProfileService.clear();
    userDebtorService.clear();
    if (window.Auth0Enabled) {
      angularAuth0.logout({ clientID: window.Auth0ClientId, returnTo: window.Auth0Callback });
    } else {
      if (window.ENABLE_REACT_LOGIN) {
        return redirectTo('/v2/#/');
      }
      deferred.resolve();
    }

    return deferred.promise;
  }

  function refresh(accessToken) {
    let data = {
      client_id: APP_CONFIG.client.id,
      client_secret: APP_CONFIG.client.secret,
      grant_type: 'refresh_token',
      refresh_token: accessToken,
    };

    let req = {
      method: 'POST',
      url: APP_CONFIG.api.token,
      headers: {
        'Content-type': 'application/x-www-form-urlencoded; charset=utf-8',
      },
      data: $httpParamSerializer(data),
    };

    return $http(req).then(function (response) {
      let auth = response.data;
      auth.expires_at = moment().add(auth.expires_in, 's').format('x');
      localStorage.setItem('oauth', JSON.stringify(auth));

      return getOauth();
    });
  }

  function signUp(registration) {
    let req = {
      isPublic: true,
      withCredentials: true,
      method: 'POST',
      url: apiService.getUrl('signUp'),
      headers: {
        'Content-type': 'application/json',
      },
      data: registration,
    };

    return $http(req);
  }

  function forgot(email) {
    let req = {
      isPublic: true,
      method: 'POST',
      url: apiService.getUrl('forgotPassword', { email }),
      headers: {
        'Content-type': 'application/json',
      },
    };

    return $http(req);
  }

  function reset(token, credentials) {
    let req = {
      isPublic: true,
      method: 'PUT',
      url: apiService.getUrl('resetPassword', { token }),
      headers: {
        'Content-type': 'application/json',
      },
      data: credentials,
    };

    return $http(req);
  }

  function getOauth() {
    let accessToken = $cookies.get('LIGHTNINGBOOK_API_ACCESSTOKEN');

    if (accessToken) {
      let oauthData = { access_token: accessToken, expires_at: moment().add(14, 'days').format('x') };
      localStorage.setItem('oauth', JSON.stringify(oauthData));
    }

    return JSON.parse(localStorage.getItem('oauth'));
  }

  function clearOauth() {
    $cookies.remove('LIGHTNINGBOOK_API_ACCESSTOKEN');
    $cookies.remove('.AspNet.Application');

    const localStorageKeys = Object.keys(localStorage);
    const auth0Namespace = 'com.auth0.auth';
    localStorage.removeItem(oauthImpersonatorKey);
    localStorageKeys.forEach((key) => {
      if (key.includes(auth0Namespace)) {
        localStorage.removeItem(key);
      }
    });

    return localStorage.removeItem('oauth');
  }

  function getAccessToken() {
    let oauth = service.getOauth();

    return oauth ? oauth.access_token : '';
  }

  function getRefreshToken() {
    let oauth = service.getOauth();

    return oauth ? oauth.refresh_token : '';
  }

  function isAuthenticated() {
    let oauth = service.getOauth();

    return oauth && oauth.expires_at && oauth.expires_at > Date.now();
  }

  function redirectTo(url) {
    return new Promise((resolve) => {
      window.location.href = url;
      window.addEventListener('unload', resolve);
    });
  }
  function revertImpersonation() {
    let deferred = $q.defer();
    const oauthImpersonator = JSON.parse(localStorage.getItem(oauthImpersonatorKey));
    if (oauthImpersonator) {
      //Log back in as imperonator
      storeAuth(oauthImpersonator);
      localStorage.removeItem(oauthImpersonatorKey);
      settingsService.get(true).then(() => {
        deferred.resolve(true);
      });
    } else {
      deferred.resolve(false);
    }
    return deferred.promise;
  }
  function loginImpersonation(username) {
    let deferred = $q.defer();
    //Check if already impersonated
    const oauthImpersonator = JSON.parse(localStorage.getItem(oauthImpersonatorKey));
    if (oauthImpersonator) {
      //Log back in as imperonator
      storeAuth(oauthImpersonator);
    }
    $http
      .get(APP_CONFIG.api.impersonateUser + '/ImpersonateUser?username=' + encodeURIComponent(username))
      .then(function (response, status, header) {
        if (!response || !response.data) {
          deferred.reject({ ErrorMessage: 'No server response', Status: 'Error' });
          return;
        }
        if (response.data.Status !== 'Success') {
          deferred.reject(response.data);
          return;
        }
        if (!oauthImpersonator) {
          const oauthImpersonator = service.getOauth();
          oauthImpersonator.UserDetails = settingsService.settings.UserDetails;
          localStorage.setItem(oauthImpersonatorKey, JSON.stringify(oauthImpersonator));
        }
        storeAuth(response.data);
        settingsService.get(true).then(() => {
          deferred.resolve(response.data);
        });
      })
      .catch((data) => {
        deferred.reject(data);
      });

    return deferred.promise;
  }
  function encodeUsername(username) {
    return username.replace('+', '|plus|');
  }
  function decodeUsername(username) {
    return username.replace('|plus|', '+');
  }
  function getImperonationOauth() {
    return JSON.parse(localStorage.getItem(oauthImpersonatorKey));
  }

  return service;
};

authService.$inject = $inject;

export default authService;



// WEBPACK FOOTER //
// ./src/services/authService.js